import type {
  ExpenseBudgetItem,
  ProjectRevenueDetailQuery,
} from "@src/__generated__/urql-graphql";
import { clone } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import { EMPTY_SUMMARY } from ".";
import type { DetailSummary, TProject } from "./types";

export class ProjectReportBaseModel {
  id: TProject["id"];
  title: TProject["title"];
  code: TProject["code"];
  @observable note: TProject["note"];
  archived_at: TProject["archived_at"];
  finished_at: TProject["finished_at"];
  brand: TProject["brand"];

  gross_income_for_date_range: TProject["gross_income_for_date_range"];

  projectManager: TProject["projectManager"];
  status: TProject["status"];

  constructor(src: TProject) {
    makeObservable(this);
    this.id = src.id;
    this.title = src.title;
    this.code = src.code;
    this.note = src.note;
    this.archived_at = src.archived_at;
    this.finished_at = src.finished_at;
    this.brand = src.brand;
    this.projectManager = src.projectManager;
    this.status = src.status;
    this.gross_income_for_date_range = src.gross_income_for_date_range;
  }
}

type ProjectRevenueDetailExpenseBudgetItem =
  ProjectRevenueDetailQuery["projectRevenueDetail"]["expensesStructured"][0]["budgetItems"][0];
type ProjectRevenueDetailExpenseBudgetItemStats =
  ProjectRevenueDetailExpenseBudgetItem["expenseBudgetItem"]["stats"];

export class ProjectReportDetail {
  @observable rows: {
    id: string;
    type?: ExpenseBudgetItem["type"];
    title: string;
    note?: string | null;
    completed_at?: Date;
    stats: {
      billings?: ProjectRevenueDetailExpenseBudgetItem["billings_for_date_range"];
      profit: ProjectRevenueDetailExpenseBudgetItem["profit_for_date_range"];
      profit_percent: ProjectRevenueDetailExpenseBudgetItemStats["profit_percent"];
      real_expenses?: ProjectRevenueDetailExpenseBudgetItemStats["real_expenses"];
    };
  }[] = [];

  constructor(
    budgetItems:
      | ProjectRevenueDetailQuery["projectRevenueDetail"]["ourWork"]
      | ProjectRevenueDetailExpenseBudgetItem[],
  ) {
    makeObservable(this);

    this.rows = budgetItems.map((budgetItem) => {
      if ("ourWorkBudgetItem" in budgetItem) {
        return {
          id: budgetItem.ourWorkBudgetItem.id,
          title: budgetItem.ourWorkBudgetItem.title,
          note: budgetItem.ourWorkBudgetItem.note,
          stats: {
            profit_percent:
              budgetItem.ourWorkBudgetItem.stats.net_margin_percent,
            profit: budgetItem.revenue_for_date_range,
            billings: budgetItem.billings_for_date_range,
          },
        };
      } else {
        return {
          id: budgetItem.expenseBudgetItem.id,
          note: budgetItem.expenseBudgetItem.note,
          type: budgetItem.expenseBudgetItem.type,
          title: budgetItem.expenseBudgetItem.title,
          stats: {
            real_expenses: budgetItem.expenseBudgetItem.stats.real_expenses,
            profit_percent: budgetItem.expenseBudgetItem.stats.profit_percent,
            profit: budgetItem.profit_for_date_range,
            billings: budgetItem.billings_for_date_range,
          },
          completed_at: budgetItem.expenseBudgetItem.completed_at ?? undefined,
        };
      }
    });
  }

  @computed get summary(): DetailSummary {
    const sum = clone(EMPTY_SUMMARY);
    if (!this.rows.length) return sum;

    const rowCount = this.rows.length;

    for (let i = 0; i < rowCount; i++) {
      const row = this.rows[i];

      sum.billings += row.stats.billings ?? 0;
      sum.netProfit += row.stats.profit ?? 0;
      sum.netMargin += row.stats.profit_percent;
    }

    sum.netMargin /= this.rows.reduce((prev, curr) => {
      if (curr.stats.profit_percent === 0) return prev;
      return prev + 1;
    }, 0);

    return sum;
  }
}
